/**
 * The workshop film's soundtrack: a saw rasping on every stroke the picture draws, the
 * offcut clattering down, a dog's sneeze, wet concrete sliding off a barrow, a paw pressed
 * into it at every print — and a marimba in D under the whole of it from the designer on,
 * so the channel's second film is not the first one's tune again.
 *
 * Every cue is read off the timing the picture is drawn from (`workshop.ts`, `shed.ts`,
 * `workshopProps.ts`): the saw on `strokeStarts`, the hammer on `hammerBlows`, a squelch on
 * each of `PRINTS`, the pieces of the frame on `landings`. `test/film.test.ts` holds them
 * to each other.
 *
 * The music is twelve bars from the designer to the end card, as the shed film's is: the
 * last chord lands as the title block does, and rings out. The bass skips where the shed
 * film's walks, and the build's kick drops out for the quiet of the finished shop.
 */

import { designSound } from "./designer.js";
import { driveSound, shopSound } from "./errands.js";
import type { Site } from "./film.js";
import { wordsSound } from "./props.js";
import { landings } from "./shed.js";
import type { Mix } from "./sound.js";
import { BOUNCE_LANDS } from "./timing.js";
import {
  AT,
  DESIGN,
  INSIDE,
  KITCHEN,
  OUTSIDE,
  PAWS,
  PLAN,
  POUR,
  PRINTS,
  SHOPPING,
  SPOTS,
  STEPS,
  WIPES,
  WORKSHOP,
  captions,
  designState,
  hammerBlows,
} from "./workshop.js";
import { strokeStarts } from "./workshopProps.js";
import { raisingSound } from "./yard.js";

export function workshopScore(mix: Mix, site: Site): void {
  kitchen(mix, AT.kitchen);
  design(mix, AT.design);
  // A step up the scale of D for each line onto the cart.
  shopSound(mix, AT.shop, SHOPPING, [74, 76, 78, 81, 83, 86, 88]);
  driveSound(mix, AT.drive, AT.build - AT.drive);
  build(mix, AT.build);
  inside(mix, AT.inside);
  outside(mix, AT.outside);
  wordsSound(mix, captions(site), WIPES);
  music(mix);
}

// ---- the kitchen -------------------------------------------------------------------------

function kitchen(mix: Mix, at: number): void {
  const pan = -0.35;
  for (const stroke of strokeStarts(...KITCHEN.sawing, KITCHEN.rate)) {
    mix.saw(at + stroke.at, stroke.push, stroke.length, { gain: 1.6, pan });
  }
  // The offcut: down onto the floor, a bounce, and still.
  const [drops, dropped] = KITCHEN.drop;
  const lands = drops + BOUNCE_LANDS * (dropped - drops);
  mix.thud(at + lands, { gain: 0.5, weight: 0.8, pan });
  mix.tap(at + lands, { gain: 0.5, pan, pitch: 0.7 });
  mix.tap(at + dropped, { gain: 0.25, pan, pitch: 0.8 });
  // The fruit bowl, jumping on the table and coming down again.
  mix.clank(at + dropped + 0.3, { gain: 0.12, base: 1400, pan: 0.15 });
  // The cloud, billowing out, and the dog's opinion of it.
  const [billows, billowed] = KITCHEN.billow;
  mix.whoosh(at + billows, billowed - billows + 0.3, { from: 220, to: 1100, gain: 0.7 });
  const [sneezes, landed] = KITCHEN.sneeze;
  mix.sneeze(at + sneezes, { gain: 0.9, pan: 0.45 });
  mix.thud(at + landed, { gain: 0.25, weight: 0.6, pan: 0.45 });

  // A thought, bubble by bubble; the workshop it dreams of; and in.
  const [thinks, thought] = KITCHEN.think;
  [0, 0.12, 0.24].forEach((k, i) => mix.pop(at + thinks + k * (thought - thinks), { gain: 0.45, pitch: 1.2 + i * 0.25, pan: 0.15 }));
  mix.pop(at + thinks + 0.36 * (thought - thinks), { gain: 0.5, pitch: 0.9, pan: 0.25 });
  const [dreams, dreamt] = KITCHEN.vision;
  const rising = [62, 66, 69, 74, 78, 81];
  rising.forEach((note, i) => mix.glock(at + dreams + (i / rising.length) * (dreamt - dreams), note, 0.45 + i * 0.06, 0.15));
  mix.glock(at + dreamt, 86, 0.55, 0.15);
  const [zooms, zoomed] = KITCHEN.zoom;
  mix.whoosh(at + zooms, zoomed - zooms, { from: 250, to: 3600, gain: 0.6, pan: 0.1 });
}

// ---- designing and buying ------------------------------------------------------------------

function design(mix: Mix, at: number): void {
  const pan = 0.1;
  const presses = [DESIGN.widthDrag[0], DESIGN.lengthDrag[0], DESIGN.slabClick, DESIGN.studClick, ...DESIGN.windowClicks];
  for (const press of presses) {
    mix.click(at + press, { gain: 0.9, pan });
  }
  for (const release of [DESIGN.widthDrag[1], DESIGN.lengthDrag[1]]) {
    mix.click(at + release, { gain: 0.5, pan });
  }
  // A detent at every foot the sliders pass.
  let width = Math.floor(designState(0).width);
  let length = Math.floor(designState(0).length);
  for (let u = 0; u < DESIGN.slabClick; u += 0.002) {
    const state = designState(u);
    if (Math.floor(state.width) !== width || Math.floor(state.length) !== length) {
      mix.tick(at + u, { gain: 0.3, pitch: 1.5, pan });
      width = Math.floor(state.width);
      length = Math.floor(state.length);
    }
  }
  // The shed on screen settling onto its slab; each window popping into the wall.
  mix.thud(at + DESIGN.slabClick + 0.04, { gain: 0.3, weight: 1.4, pan: 0.3 });
  DESIGN.windowClicks.forEach((click, i) => mix.pop(at + click + 0.03, { gain: 0.4, pitch: 1.4 + i * 0.2, pan: 0.3 }));
  designSound(mix, at, DESIGN, pan);
}

// ---- the build -------------------------------------------------------------------------------

function build(mix: Mix, at: number): void {
  const pieces = landings(WORKSHOP);
  const slab = (k: number): number => at + PLAN.slab[0] + k * (PLAN.slab[1] - PLAN.slab[0]);
  const sills = (k: number): number => at + PLAN.sills[0] + k * (PLAN.sills[1] - PLAN.sills[0]);

  // The guide's page turns at every step, and a puff of dust each time the maker moves on.
  for (const step of STEPS.slice(1)) {
    mix.whoosh(at + step.at, 0.14, { from: 2400, to: 5200, gain: 0.16, pan: -0.6 });
  }
  for (const spot of SPOTS.slice(1, -1)) {
    mix.whoosh(at + spot, 0.16, { from: 500, to: 1400, gain: 0.12 });
  }

  // The forms dropped around the footprint; the concrete poured, and struck off level.
  pieces.forms.forEach((k) => {
    mix.thud(slab(k), { gain: 0.5, weight: 1 });
    mix.tap(slab(k), { gain: 0.18, pitch: 0.75 });
  });
  const [pours, poured] = POUR;
  mix.roll(at + pours - 0.1, poured - pours, { gain: 0.35, pan: -0.1 });
  mix.pour(at + pours, poured - pours, { gain: 0.9, pan: 0.05 });
  mix.scrape(at + poured, PLAN.slab[1] - poured, { gain: 0.8, bright: 0.75 });

  // The sills laid and bolted — and the dog across the wet concrete, a paw at a time.
  pieces.sills.forEach((k) => {
    mix.thud(sills(k), { gain: 0.45, weight: 0.9, pan: -0.15 });
    mix.clank(sills(k) + 0.04, { gain: 0.1, base: 2200, pan: -0.15 });
  });
  mix.whoosh(at + PAWS.push[0], PAWS.push[1] - PAWS.push[0], { from: 300, to: 900, gain: 0.2 });
  for (const print of PRINTS) {
    mix.squelch(at + print.at, { gain: 0.7, pan: Math.max(-0.6, Math.min(0.6, (print.x - 6) / 10)) });
  }
  // Uh-oh: down a half step at a time, out of key on purpose.
  [74, 73, 72].forEach((note, i) => mix.glock(at + PAWS.cross[0] + 0.3 + i * 0.16, note, 0.35, -0.2));

  raisingSound(mix, at, WORKSHOP, PLAN, pieces, [81, 86]);
  hammerBlows().forEach((blow, i) => mix.tap(at + blow.at, { gain: 0.72, pan: blow.pan, pitch: 0.95 + ((i * 7) % 5) * 0.025 }));
}

// ---- inside, and out ------------------------------------------------------------------------

function inside(mix: Mix, at: number): void {
  for (const stroke of strokeStarts(...INSIDE.sanding, INSIDE.rate)) {
    mix.scrape(at + stroke.at, stroke.length, { gain: 0.45, bright: 3, pan: -0.3 });
  }
  const [lifts, lifted] = INSIDE.lift;
  mix.whoosh(at + lifts, lifted - lifts, { from: 300, to: 900, gain: 0.18, pan: -0.3 });
  const [blows, blown] = INSIDE.blow;
  mix.whoosh(at + blows, blown - blows, { from: 900, to: 2600, gain: 0.3, pan: -0.3 });
  mix.glock(at + INSIDE.shine[0] + 0.1, 93, 0.28, -0.3);
}

function outside(mix: Mix, at: number): void {
  // The bird: wings on the way in, a song from the birdhouse roof.
  const [flies, landed] = OUTSIDE.bird;
  mix.flutter(at + flies, landed - flies, { gain: 0.22, pan: -0.2 });
  mix.chirp(at + OUTSIDE.song[0], { gain: 0.8, pan: -0.35 });
  mix.chirp(at + OUTSIDE.song[0] + 0.38, { gain: 0.65, pan: -0.35 });
  // The title block slides up and settles.
  const [rises] = OUTSIDE.card;
  mix.whoosh(at + rises, 0.42, { from: 200, to: 1300, gain: 0.45 });
  mix.thud(at + rises + 0.21, { gain: 0.35, weight: 1.3 });
}

// ---- the music ---------------------------------------------------------------------------------

interface Chord {
  bass: number;
  tones: readonly [number, number, number, number];
}

const D: Chord = { bass: 50, tones: [62, 66, 69, 74] };
const A: Chord = { bass: 45, tones: [61, 64, 69, 73] };
const BM: Chord = { bass: 47, tones: [62, 66, 71, 74] };
const G: Chord = { bass: 43, tones: [62, 67, 71, 74] };

/** Twelve bars, and a thirteenth chord to end on. */
const BARS: readonly Chord[] = [D, A, BM, G, D, A, G, A, D, A, BM, G];

/** The order the arpeggio climbs a chord's four tones in, eight to a bar. */
const CLIMB = [0, 1, 2, 3, 2, 1, 2, 3] as const;

/** When the music starts and when its last chord lands: the designer, and the end card. */
export const MUSIC = { start: AT.design, end: AT.outside + OUTSIDE.card[0] } as const;

function music(mix: Mix): void {
  const bar = (MUSIC.end - MUSIC.start) / BARS.length;
  const beat = bar / 4;
  const eighth = beat / 2;
  BARS.forEach((chord, b) => {
    const t0 = MUSIC.start + b * bar;
    // The kick drives the build, and drops out for the quiet of the finished shop.
    const building = t0 >= AT.build - beat && t0 < AT.inside - beat;
    // A bass that skips: on the one, the and of two, and up the octave between.
    mix.mallet(t0, chord.bass, 0.85, { ring: 1.5 });
    mix.mallet(t0 + 3 * eighth, chord.bass + 12, 0.45, { ring: 1.1 });
    mix.mallet(t0 + 5 * eighth, chord.bass + 7, 0.5, { ring: 1.2 });
    CLIMB.forEach((tone, e) => {
      const velocity = e === 0 ? 0.6 : 0.36 + 0.08 * ((e * 3 + b * 5) % 4);
      mix.mallet(t0 + e * eighth, chord.tones[tone], velocity, { pan: e % 2 === 0 ? 0.22 : -0.22 });
      if (e % 2 === 1 || building) {
        mix.shaker(t0 + e * eighth, e % 2 === 1 ? 0.5 : 0.28);
      }
    });
    if (building) {
      mix.kick(t0, 0.55);
      mix.kick(t0 + 2 * beat, 0.45);
    }
  });
  // The last chord, rolled from the bottom up, with a bell on top: it rings to the end.
  [38, 50, 57, 62, 66, 69, 74].forEach((note, i) => mix.mallet(MUSIC.end + i * 0.028, note, 0.72 - i * 0.03, { ring: 2.6 }));
  mix.glock(MUSIC.end + 0.06, 81, 0.45);
  mix.glock(MUSIC.end + 0.12, 86, 0.4);
}
